import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Switch,
  Button,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Alert,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { BookingPreferences } from "shared/types";

interface BookingPreferencesSectionProps {
  data: BookingPreferences;
  onSave: (data: BookingPreferences) => void;
}

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#178B8E" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#178B8E",
  },
};

const MIN_NOTICE_OPTIONS = [
  { value: "same-day", label: "Same day" },
  { value: "1-day", label: "1 day" },
  { value: "2-days", label: "2 days" },
  { value: "3-days", label: "3 days" },
  { value: "1-week", label: "1 week" },
];

const MAX_ADVANCE_OPTIONS = [
  { value: "1-week", label: "1 week" },
  { value: "2-weeks", label: "2 weeks" },
  { value: "1-month", label: "1 month" },
  { value: "3-months", label: "3 months" },
  { value: "6-months", label: "6 months" },
  { value: "1-year", label: "1 year" },
];

const MIN_STAY_OPTIONS = [
  { value: "1-night", label: "1 night" },
  { value: "2-nights", label: "2 nights" },
  { value: "3-nights", label: "3 nights" },
  { value: "1-week", label: "1 week" },
];

const MAX_STAY_OPTIONS = [
  { value: "1-week", label: "1 week" },
  { value: "2-weeks", label: "2 weeks" },
  { value: "1-month", label: "1 month" },
  { value: "no-limit", label: "No limit" },
];

const BookingPreferencesSection: React.FC<BookingPreferencesSectionProps> = ({
  data,
  onSave,
}) => {
  const [draft, setDraft] = useState<BookingPreferences>(data);

  const handleField = (
    field: keyof BookingPreferences,
    value: string | boolean,
  ) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <CalendarMonthIcon sx={{ color: "#178B8E", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          Booking Preferences
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Set your general booking rules. Manage specific dates in your Calendar.
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            label="Minimum notice required"
            value={draft.minNotice}
            onChange={(e) => handleField("minNotice", e.target.value)}
            size="small"
            helperText="How much notice do you need before a booking starts?"
          >
            {MIN_NOTICE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            label="Maximum booking advance"
            value={draft.maxAdvance}
            onChange={(e) => handleField("maxAdvance", e.target.value)}
            size="small"
            helperText="How far ahead can clients book?"
          >
            {MAX_ADVANCE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            label="Minimum stay length"
            value={draft.minStay}
            onChange={(e) => handleField("minStay", e.target.value)}
            size="small"
          >
            {MIN_STAY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            label="Maximum stay length"
            value={draft.maxStay}
            onChange={(e) => handleField("maxStay", e.target.value)}
            size="small"
          >
            {MAX_STAY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2.5 }} />

      {/* Auto-accept */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={500}>
            Auto-accept compatible requests
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Automatically accept bookings that match your preferences and
            availability
          </Typography>
        </Box>
        <Switch
          checked={draft.autoAccept}
          onChange={() => handleField("autoAccept", !draft.autoAccept)}
          size="small"
          sx={switchSx}
        />
      </Box>

      {draft.autoAccept && (
        <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
          Auto-accept will confirm bookings immediately without your review.
          Make sure your calendar and preferences are always up to date.
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => onSave(draft)}
          sx={{
            textTransform: "none",
            backgroundColor: "#178B8E",
            "&:hover": { backgroundColor: "#146f72" },
          }}
        >
          Save Changes
        </Button>
      </Box>
    </Paper>
  );
};

export default BookingPreferencesSection;
